import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ContentHeader from '../../components/UI/ContentHeader';
import { api } from '../../services';
import { useContextInfo } from '../../context';
import * as S from '../BillingPage/styled';

const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ACCEPT = '.pdf,application/pdf';

const STATUS_KEYS = {
  entwurf: 'billing.utility_bill_status_entwurf',
  veröffentlicht: 'billing.utility_bill_status_veroeffentlicht',
  archiviert: 'billing.utility_bill_status_archiviert'
};

function getDocumentDisplayName(doc) {
  if (!doc) return null;
  const arr = Array.isArray(doc) ? doc : [doc];
  const first = arr[0];
  if (!first) return null;
  // Bei Junction utility_bill_files gibt es nur directus_files_id, kein filename_download
  return first.filename_download || first.filename || (first.directus_files_id ? null : first.id) || null;
}

const BillingElectricityUploadPage = () => {
  const { t } = useTranslation();
  const { userInfo } = useContextInfo();
  const { info: userInfoData } = userInfo || {};

  const [utilityBills, setUtilityBills] = useState([]);
  const [chargepoints, setChargepoints] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [selectedChargepoint, setSelectedChargepoint] = useState('');
  const [selectedTariff, setSelectedTariff] = useState('');
  const [electricityBillRequired] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    const controller = new AbortController();
    const { signal } = controller;
    const user = userInfoData?.email;
    const password = userInfoData?.passwordHash;
    setLoading(true);
    setLoadError(null);
    try {
      const [chargepointsRes, tariffsRes] = await Promise.all([
        api.directus.getChargepoints({ signal, user, password }).catch(() => []),
        api.directus.getUserElectricityTariffs({ signal, user, password }).catch(() => [])
      ]);
      const chargepointsList = Array.isArray(chargepointsRes) ? chargepointsRes : [];
      const tariffsList = Array.isArray(tariffsRes) ? tariffsRes : [];
      const billsRes = await api.directus.getUtilityBills({ orgUnit: null, signal, user, password });
      setUtilityBills(Array.isArray(billsRes) ? billsRes : []);
      setChargepoints(chargepointsList);
      setTariffs(tariffsList);
      if (chargepointsList.length === 1) setSelectedChargepoint(String(chargepointsList[0].id));
      if (tariffsList.length === 1) setSelectedTariff(String(tariffsList[0].id));
    } catch (err) {
      console.error('[BillingElectricityUploadPage] load error', err);
      setLoadError(err?.message || t('billing.electricity_upload_dialog_error'));
      setUtilityBills([]);
      setChargepoints([]);
      setTariffs([]);
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, [userInfoData?.email, userInfoData?.passwordHash, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const validateFile = useCallback(
    (f) => {
      if (!f) return null;
      if (f.type !== 'application/pdf') return t('billing.electricity_upload_dialog_error');
      if (f.size > MAX_SIZE_BYTES) return t('billing.electricity_upload_dialog_error');
      return null;
    },
    [t]
  );

  const handleFile = useCallback(
    async (f) => {
      const err = validateFile(f);
      setError(err || null);
      if (err) return;
      const user = userInfoData?.email;
      const password = userInfoData?.passwordHash;
      if (!user || !password) {
        setError(t('billing.electricity_upload_dialog_error'));
        return;
      }
      setUploading(true);
      setError(null);
      try {
        const { id: fileId } = await api.directus.uploadFile(f, { user, password });
        const chargepointId = selectedChargepoint ? parseInt(selectedChargepoint, 10) : null;
        const tariffId = selectedTariff ? parseInt(selectedTariff, 10) : null;
        const orgUnit = chargepoints.find((c) => String(c.id) === selectedChargepoint)?.org_unit
          ?? tariffs.find((tr) => String(tr.id) === selectedTariff)?.org_unit
          ?? null;
        const created = await api.directus.createUtilityBill(
          {
            chargepoint: chargepointId,
            user_electricity_tariff: tariffId,
            org_unit: orgUnit,
            status: 'entwurf'
          },
          { user, password }
        );
        await api.directus.createUtilityBillFile(created.id, fileId, { user, password });
        await loadData();
      } catch (err) {
        console.error('[BillingElectricityUploadPage] upload/create error', err);
        setError(err?.message || t('billing.electricity_upload_dialog_error'));
      } finally {
        setUploading(false);
      }
    },
    [validateFile, userInfoData, selectedChargepoint, selectedTariff, chargepoints, tariffs, loadData, t]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const onInputChange = (e) => {
    const f = e.target?.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  const statusLabel = (status) => {
    const key = STATUS_KEYS[status] || 'billing.utility_bill_status_unknown';
    return t(key);
  };

  return (
    <S.Wrapper
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.3 }}
    >
      <ContentHeader showTab={false} />
      <S.Container>
        <S.Info>{t('billing.electricity_upload_title')}</S.Info>
        <S.InfoSubtitle>{t('billing.electricity_upload_info')}</S.InfoSubtitle>

        {loadError && (
          <p style={{ marginBottom: 16, fontSize: 14, color: '#E53935' }}>{loadError}</p>
        )}

        <S.ElectricityUploadSection $highlight={electricityBillRequired}>
          {electricityBillRequired && (
            <S.ElectricityUploadRequiredHint>
              {t('billing.electricity_upload_required_hint')}
            </S.ElectricityUploadRequiredHint>
          )}

          {(chargepoints.length > 1 || tariffs.length >= 1) && (
            <S.ElectricityFormRow>
              {chargepoints.length > 1 && (
                <S.ElectricityFormField>
                  <S.ElectricityFormLabel>{t('billing.chargepoint_label')}</S.ElectricityFormLabel>
                  <S.ElectricitySelect
                    value={selectedChargepoint}
                    onChange={(e) => setSelectedChargepoint(e.target.value)}
                  >
                    <option value="">{t('billing.select_chargepoint')}</option>
                    {chargepoints.map((cp) => (
                      <option key={cp.id} value={cp.id}>
                        {cp.title || `Ladepunkt ${cp.id}`}
                      </option>
                    ))}
                  </S.ElectricitySelect>
                </S.ElectricityFormField>
              )}
              {tariffs.length >= 1 && (
                <S.ElectricityFormField>
                  <S.ElectricityFormLabel>{t('billing.tariff_label')}</S.ElectricityFormLabel>
                  <S.ElectricitySelect
                    value={selectedTariff}
                    onChange={(e) => setSelectedTariff(e.target.value)}
                  >
                    <option value="">{t('billing.select_tariff')}</option>
                    {tariffs.map((tr) => (
                      <option key={tr.id} value={tr.id}>
                        {tr.provider || ''} {tr.price_per_kwh != null ? `(${Number(tr.price_per_kwh).toFixed(2)} €/kWh)` : ''}
                      </option>
                    ))}
                  </S.ElectricitySelect>
                </S.ElectricityFormField>
              )}
            </S.ElectricityFormRow>
          )}

          <S.ElectricityUploadDropzone
            className={dragOver ? 'drag-over' : ''}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => !uploading && document.getElementById('electricity-upload-input')?.click()}
            style={uploading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
          >
            <input
              id="electricity-upload-input"
              type="file"
              accept={ACCEPT}
              onChange={onInputChange}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            {uploading ? '…' : t('billing.electricity_upload_dialog_drop')}
          </S.ElectricityUploadDropzone>
          {error && (
            <p style={{ marginTop: 12, fontSize: 14, color: '#E53935' }}>{error}</p>
          )}
        </S.ElectricityUploadSection>

        <S.ElectricityHistoryWidget>
          <S.ElectricityUploadHistoryTitle>{t('billing.electricity_upload_history_title')}</S.ElectricityUploadHistoryTitle>
          {loading ? (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-dark-gray, #666)' }}>…</p>
          ) : utilityBills.length === 0 ? (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-dark-gray, #666)' }}>
              {t('billing.electricity_upload_history_empty')}
            </p>
          ) : (
            <S.ElectricityUploadHistoryList>
              {utilityBills.map((item) => (
                <S.ElectricityUploadHistoryItem key={item.id}>
                  <span>{getDocumentDisplayName(item.document) || 'Stromrechnung'}</span>
                  <span>{item.date_created ? moment(item.date_created).format('DD.MM.YYYY') : ''}</span>
                  <span>{statusLabel(item.status)}</span>
                </S.ElectricityUploadHistoryItem>
              ))}
            </S.ElectricityUploadHistoryList>
          )}
        </S.ElectricityHistoryWidget>
      </S.Container>
    </S.Wrapper>
  );
};

export default BillingElectricityUploadPage;
